import {
  ApprovalMasterModel,
  ApprovalMasterMutationInput,
} from 'api-hooks/approval-master';
import { EnumList } from 'common/constants/enum';
import formSetErrors from 'common/helpers/form-setError';
import notification from 'components/common/notification';
import { Form, FormState } from 'components/elements';
import {
  FullContainer,
  HalfContainer,
  SectionContainer,
} from 'components/styles';
import FormHeader from 'components/widgets/form-header';
import useDestroyAction from 'hooks/use-destroy-action';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useYupValidationResolver from 'hooks/use-yup-validation-resolver';
import EnumSelectInput from 'legacy-modules/master/select-input/enum';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

import { ApprovalMasterFormType } from './form-type';
import RolesSection from './roles';

interface Props {
  approval?: ApprovalMasterModel;
  onSubmit: (
    input: ApprovalMasterMutationInput,
    form: ReturnType<typeof useForm>,
  ) => Promise<ApprovalMasterModel | undefined>;
}

export default function ApprovalMasterForm(props: Props) {
  const { t } = useTranslation();
  const { approval } = props;
  const { asPath } = useRouter();

  const defaultValues = React.useMemo<ApprovalMasterFormType>(() => {
    return {
      model: approval?.model || '',
      approvalDetails:
        approval?.approvalDetails.map((item) => ({
          id: item.id,
          roleId: item.role.id,
          order: item.order,
        })) || [],
    };
  }, [approval]);

  const yupSchema = React.useMemo(
    () =>
      Yup.object().shape({
        model: Yup.string().nullable().required(),
        approvalDetails: Yup.array(
          Yup.object({
            roleId: Yup.string().nullable().required(),
          }),
        ).required(),
      }),
    [],
  );

  const resolver = useYupValidationResolver(yupSchema);

  const methods = useForm<ApprovalMasterFormType>({
    resolver,
    defaultValues,
    mode: 'onChange',
  });

  const onSubmit = React.useCallback(
    async (values) => {
      try {
        values.approvalDetails = values.approvalDetails?.map((item, idx) => {
          return { ...item, order: idx + 1 };
        });

        await props.onSubmit(values, methods as any);
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods, props],
  );

  const permission = useGetCrudPermission('approvals');
  const { onDelete, isLoading: isDeleteLoading } = useDestroyAction(
    'approvals',
    approval?.id,
  );

  const customViewName =
    approval && [t('common:approval'), approval.model].join(' - ');

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!approval}
      customViewName={customViewName}
      keyName={asPath}
    >
      <FormState>
        {({ editable }) => (
          <FormHeader
            data={approval}
            noDelete={editable || !permission.delete}
            noEdit={!permission.update}
            title={
              approval
                ? customViewName ?? t('common:approval')
                : t('common:add_extra', { extra: t('common:approval') })
            }
            onDelete={onDelete}
            isDeleteLoading={isDeleteLoading}
          />
        )}
      </FormState>

      <SectionContainer>
        <FullContainer>
          <HalfContainer>
            <EnumSelectInput
              name="model"
              label={t('common:model')}
              placeholder={t('common:model')}
              required
              enumType={EnumList.ApprovalableType}
              disabled={!!approval}
            />
          </HalfContainer>
          <RolesSection />
        </FullContainer>
      </SectionContainer>
    </Form>
  );
}
